import { useState } from 'react'
import './App.css'
import { useDriftAnalysis } from './hooks/useDriftAnalysis'
import { getSeverityBadgeClass, formatDuration } from './services/api'

/* Inline SVG Icons - Premium, no dependencies */
const Icons = {
  shield: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  rocket: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
    </svg>
  ),
  gitPR: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="18" r="3"/><circle cx="6" cy="6" r="3"/>
      <path d="M13 6h3a2 2 0 0 1 2 2v7"/><line x1="6" y1="9" x2="6" y2="21"/>
    </svg>
  ),
  settings: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
    </svg>
  ),
  search: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  ),
  refresh: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
  ),
  stop: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    </svg>
  ),
  file: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  ),
  alert: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  clock: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  check: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  chevron: (down) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: down ? 'rotate(180deg)' : 'rotate(0deg)' }}>
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  ),
  x: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
    </svg>
  ),
  sparkle: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/>
    </svg>
  ),
}

function App() {
  const [repoUrl, setRepoUrl] = useState('')
  const [scanMode, setScanMode] = useState('auto') // 'auto', 'pr', 'manual'
  const [prNumber, setPrNumber] = useState('')
  const [baseRef, setBaseRef] = useState('main')
  const [headRef, setHeadRef] = useState('')
  const [githubToken, setGithubToken] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)

  const {
    isAnalyzing,
    progress,
    result,
    error,
    currentNode,
    startScan,
    startPRScan,
    startManualAnalysis,
    cancelAnalysis,
    reset,
  } = useDriftAnalysis()

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!repoUrl.trim()) {
      alert('Please enter a repository URL')
      return
    }

    const token = githubToken.trim() || null

    if (scanMode === 'auto') {
      startScan(repoUrl, token)
    } else if (scanMode === 'pr') {
      const pr = prNumber.trim() ? parseInt(prNumber) : null
      startPRScan(repoUrl, pr, token)
    } else {
      if (!headRef.trim()) {
        alert('Please enter a head reference')
        return
      }
      startManualAnalysis(repoUrl, baseRef, headRef, token)
    }
  }

  const handleReset = () => {
    reset()
    setRepoUrl('')
    setPrNumber('')
    setBaseRef('main')
    setHeadRef('')
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <h1>{Icons.shield} DriftGuard</h1>
          <p className="tagline">AI-Powered Documentation Drift Detection</p>
        </div>
      </header>

      <main className="app-main">
        {/* Input Form */}
        <div className="input-card">
          <h2>Analyze Repository</h2>
          
          <form onSubmit={handleSubmit}>
            {/* Scan Mode Selector */}
            <div className="mode-selector">
              <button
                type="button"
                className={`mode-btn ${scanMode === 'auto' ? 'active' : ''}`}
                onClick={() => setScanMode('auto')}
              >
                {Icons.rocket} Quick Scan
              </button>
              <button
                type="button"
                className={`mode-btn ${scanMode === 'pr' ? 'active' : ''}`}
                onClick={() => setScanMode('pr')}
              >
                {Icons.gitPR} PR Scan
              </button>
              <button
                type="button"
                className={`mode-btn ${scanMode === 'manual' ? 'active' : ''}`}
                onClick={() => setScanMode('manual')}
              >
                {Icons.settings} Manual
              </button>
            </div>

            {/* Repository URL */}
            <div className="form-group">
              <label htmlFor="repoUrl">Repository URL</label>
              <input
                id="repoUrl"
                type="text"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                placeholder="https://github.com/owner/repo"
                disabled={isAnalyzing}
                required
              />
              <small>
                {scanMode === 'auto' && 'Auto-detects latest commit on default branch'}
                {scanMode === 'pr' && 'Scans pull request changes against base'}
                {scanMode === 'manual' && 'Compare specific branches, tags, or commits'}
              </small>
            </div>

            {/* PR Number (PR mode only) */}
            {scanMode === 'pr' && (
              <div className="form-group">
                <label htmlFor="prNumber">PR Number (optional)</label>
                <input
                  id="prNumber"
                  type="number"
                  value={prNumber}
                  onChange={(e) => setPrNumber(e.target.value)}
                  placeholder="Leave empty for latest open PR"
                  disabled={isAnalyzing}
                />
              </div>
            )}

            {/* Manual Refs (Manual mode only) */}
            {scanMode === 'manual' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="baseRef">Base Ref</label>
                    <input
                      id="baseRef"
                      type="text"
                      value={baseRef}
                      onChange={(e) => setBaseRef(e.target.value)}
                      placeholder="main"
                      disabled={isAnalyzing}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="headRef">Head Ref</label>
                    <input
                      id="headRef"
                      type="text"
                      value={headRef}
                      onChange={(e) => setHeadRef(e.target.value)}
                      placeholder="feature-branch"
                      disabled={isAnalyzing}
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {/* Advanced Options */}
            <div className="advanced-toggle">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="toggle-btn"
              >
                {Icons.chevron(showAdvanced)} Advanced Options
              </button>
            </div>

            {showAdvanced && (
              <div className="form-group">
                <label htmlFor="githubToken">GitHub Token (optional)</label>
                <input
                  id="githubToken"
                  type="password"
                  value={githubToken}
                  onChange={(e) => setGithubToken(e.target.value)}
                  placeholder="ghp_xxxxxxxxxxxx"
                  disabled={isAnalyzing}
                />
                <small>For private repos or higher rate limits</small>
              </div>
            )}

            {/* Action Buttons */}
            <div className="form-actions">
              {!isAnalyzing ? (
                <>
                  <button type="submit" className="btn-primary">
                    {Icons.search} Start Analysis
                  </button>
                  {result && (
                    <button type="button" onClick={handleReset} className="btn-secondary">
                      {Icons.refresh} New Analysis
                    </button>
                  )}
                </>
              ) : (
                <button type="button" onClick={cancelAnalysis} className="btn-danger">
                  {Icons.stop} Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Progress Log */}
        {progress.length > 0 && (
          <div className="progress-card">
            <h3>
              {isAnalyzing ? (
                <><div className="spinner"></div> Analysis in Progress</>
              ) : (
                <>{Icons.check} Analysis Complete</>
              )}
            </h3>
            {currentNode && (
              <div className="current-node">
                <div className="spinner"></div>
                <span>{formatNodeName(currentNode)}</span>
              </div>
            )}
            <div className="progress-log">
              {progress.map((item, index) => (
                <div key={index} className={`progress-item ${item.type}`}>
                  <span className="progress-time">
                    {new Date(item.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="progress-message">{item.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-card">
            <h3>{Icons.x} Error</h3>
            <p>{error}</p>
          </div>
        )}

        {/* Results Display */}
        {result && (
          <>
            {/* Health Score */}
            <div className="health-card">
              <h3>Documentation Health Score</h3>
              <div className="health-score-display">
                <div className={`health-score ${getHealthClass(result.health_score)}`}>
                  {result.health_score}
                  <span className="score-label">/100</span>
                </div>
                <div className="health-info">
                  <p className="health-status">{getHealthStatus(result.health_score)}</p>
                  <div className="health-stats">
                    <span>{Icons.file} {result.files_analyzed} files analyzed</span>
                    <span>{Icons.alert} {result.drifts_detected} drifts found</span>
                    <span>{Icons.clock} {formatDuration(result.execution_time_ms)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Drifts List */}
            {result.drifts && result.drifts.length > 0 ? (
              <div className="drifts-card">
                <h3>Detected Drifts ({result.drifts.length})</h3>
                <div className="drifts-list">
                  {result.drifts.map((drift, index) => (
                    <DriftItem key={index} drift={drift} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-drifts-card">
                <h3>{Icons.sparkle} No Drifts Detected</h3>
                <p>Your documentation is up to date with the code changes!</p>
              </div>
            )}
          </>
        )}
      </main>

      <footer className="app-footer">
        <p>Built with precision • Powered by LangGraph + Groq</p>
      </footer>
    </div>
  )
}

function DriftItem({ drift }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="drift-item">
      <div className="drift-header" onClick={() => setExpanded(!expanded)}>
        <div className="drift-title">
          <span className={`severity-badge ${getSeverityBadgeClass(drift.severity)}`}>
            {drift.severity}
          </span>
          <span className="drift-file">{drift.file}</span>
          <span className="drift-section">→ {drift.section}</span>
        </div>
        <button className="expand-btn">{Icons.chevron(expanded)}</button>
      </div>
      
      {expanded && (
        <div className="drift-details">
          <div className="drift-reason">
            <strong>Why it's stale</strong>
            <p>{drift.reason}</p>
          </div>
          
          <div className="drift-fix">
            <strong>Suggested fix</strong>
            <p>{drift.suggested_fix}</p>
          </div>
          
          {drift.code_changes && drift.code_changes.length > 0 && (
            <div className="drift-changes">
              <strong>Related code changes</strong>
              <ul>
                {drift.code_changes.map((change, i) => (
                  <li key={i}><code>{change}</code></li>
                ))}
              </ul>
            </div>
          )}
          
          {drift.line_range && (
            <div className="drift-lines">
              <small>Lines: {drift.line_range}</small>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function getHealthClass(score) {
  if (score >= 80) return 'excellent'
  if (score >= 60) return 'good'
  if (score >= 40) return 'fair'
  return 'poor'
}

function getHealthStatus(score) {
  if (score >= 80) return 'Excellent — Documentation is well maintained'
  if (score >= 60) return 'Good — Minor issues detected'
  if (score >= 40) return 'Fair — Several drifts need attention'
  return 'Critical — Documentation needs significant updates'
}

function formatNodeName(node) {
  const names = {
    fetch_changes: 'Fetching code changes',
    fetch_docs: 'Fetching documentation',
    analyze_drift: 'Analyzing drift with AI',
    calculate_health: 'Calculating health score',
    starting: 'Initializing...',
  }
  return names[node] || node
}

export default App

// Made with Bob
